"use client";

import type { ReactNode } from "react";
import { Mic, Monitor, Pause, Play, Square, Video, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils/cn";

const PRESS_CLASS =
  "transition-transform active:scale-[0.92] motion-reduce:transition-none motion-reduce:active:scale-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
const CONTROL_CLASS = cn("flex h-[72px] w-[72px] items-center justify-center rounded-full", PRESS_CLASS);
const RING_CONTROL_CLASS = cn(CONTROL_CLASS, "border-[3px] border-record-line bg-card text-foreground");

interface RecordButtonProps {
  state: "idle" | "recording" | "paused";
  mode: "audio" | "video" | "screen";
  onRecord: () => void;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
  onDiscard: () => void;
  disabled?: boolean;
  isLiveTranscription?: boolean;
}

function ControlSlot({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex w-[72px] flex-col items-center gap-2">
      {children}
      <span className="text-xs text-muted-foreground" aria-hidden="true">{label}</span>
    </div>
  );
}

export function RecordButton({
  state,
  mode,
  onRecord,
  onPause,
  onResume,
  onStop,
  onDiscard,
  disabled = false,
  isLiveTranscription = false,
}: RecordButtonProps) {
  const { t } = useTranslation();
  if (state === "idle") {
    const Icon = mode === "video" ? Video : mode === "screen" ? Monitor : Mic;
    return (
      <button
        type="button"
        onClick={onRecord}
        disabled={disabled}
        className={cn(
          "flex h-24 w-24 items-center justify-center rounded-full border-[3px] border-record-line disabled:pointer-events-none disabled:opacity-50",
          PRESS_CLASS,
        )}
        aria-label={t("UPLOAD.RECORD_BUTTON.START", "Start recording")}
        data-testid="recorder-start"
      >
        <span className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-record text-danger-foreground">
          <Icon className="h-[30px] w-[30px]" aria-hidden="true" />
        </span>
      </button>
    );
  }

  const pauseLabel = t("ACTION.PAUSE", "Pause");

  return (
    <div className="flex items-start justify-center gap-6 sm:gap-10">
      {!isLiveTranscription && (
        <ControlSlot label={t("ACTION.DISCARD", "Discard")}>
          <button
            type="button"
            onClick={onDiscard}
            className={RING_CONTROL_CLASS}
            aria-label={t("ACTION.DISCARD", "Discard")}
            data-testid="recorder-discard-recording"
          >
            <X className="h-7 w-7" aria-hidden="true" />
          </button>
        </ControlSlot>
      )}

      <ControlSlot label={t("RECORD.STOP", "Stop")}>
        <button
          type="button"
          onClick={onStop}
          className={cn(CONTROL_CLASS, "bg-record text-danger-foreground")}
          aria-label={t("UPLOAD.RECORD_BUTTON.STOP", "Stop recording")}
          data-testid="recorder-stop"
        >
          <Square className="h-7 w-7" aria-hidden="true" />
        </button>
      </ControlSlot>

      {state === "recording" ? (
        isLiveTranscription ? (
          <ControlSlot label={pauseLabel}>
            <span className="inline-flex" title={t("UPLOAD.RECORD_BUTTON.PAUSE_UNAVAILABLE_TITLE", "Pause is not available during live transcription")}>
              <button
                type="button"
                disabled
                className={cn(RING_CONTROL_CLASS, "cursor-not-allowed text-muted-foreground opacity-40")}
                aria-label={t("UPLOAD.RECORD_BUTTON.PAUSE_UNAVAILABLE_ARIA", "Pause unavailable during live transcription")}
                data-testid="recorder-pause-disabled"
              >
                <Pause className="h-7 w-7" aria-hidden="true" />
              </button>
            </span>
          </ControlSlot>
        ) : (
          <ControlSlot label={pauseLabel}>
            <button
              type="button"
              onClick={onPause}
              className={RING_CONTROL_CLASS}
              aria-label={t("UPLOAD.RECORD_BUTTON.PAUSE", "Pause recording")}
              data-testid="recorder-pause"
            >
              <Pause className="h-7 w-7" aria-hidden="true" />
            </button>
          </ControlSlot>
        )
      ) : (
        <ControlSlot label={t("RECORD.RESUME", "Resume")}>
          <button
            type="button"
            onClick={onResume}
            className={RING_CONTROL_CLASS}
            aria-label={t("UPLOAD.RECORD_BUTTON.RESUME", "Resume recording")}
            data-testid="recorder-resume"
          >
            <Play className="h-7 w-7" aria-hidden="true" />
          </button>
        </ControlSlot>
      )}
    </div>
  );
}
